import React, { useState } from 'react';
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableBody,
  TablePagination,
  Link,
  Box,
  useTheme,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RepairRequestInfo, SortOrder } from '../../../services/apiTypes';
import { formatDate } from '../../../_utils/utils';
import {
  ROWS_PER_PAGE_LIST,
  STATUS,
  TYPE,
  ORDER_TEXT,
} from '../../../_constants/Const';
import { ADD_BUTTON_SIZE, TABLE_HEADER } from '../../../_constants/styleConst';
import useErrorModal from '../../../hooks/useErrorModal';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import { RepairRequestListParam } from '../../../stores/repairRequestList/api';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../stores/store';
import {
  HISTORY_STATUSES,
  HISTORY_TYPE,
  INSPECTION_STATUSES,
} from '../../../_constants/inspectionConst';
import { isAdmin } from '../../../_utils/utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteRepairRequest } from '../../../stores/repairRequest/slice';
import { isRejected } from '@reduxjs/toolkit';
import ConfirmModal from '../../../_common/Modal/ConfirmModal';
import { API_CODE } from '../../../_constants/apiConst';
import useSaveHistory from '../../../hooks/useSaveHistory';

type SearchResultTableProps = {
  params: RepairRequestListParam;
  updateParams: (params: RepairRequestListParam) => void;
  page: number;
  updatePage: (page: number) => void;
  repairRequests: RepairRequestInfo[];
  totalRows: number;
  onSortChange: (sortKey: SortKeyType, order: SortOrder) => void;
  prepareInspection: (request: RepairRequestInfo) => Promise<void>;
  fetchRepairRequests: (requestParams: RepairRequestListParam) => Promise<void>;
};

/** テーブルに表示するカラム */
const resultTableColumns = [
  { id: 0, width: '10%', sortKey: 'RmaNo', label: 'LBL_RMA_NO' },
  { id: 1, width: '8%', sortKey: 'DisplayType', label: 'LBL_TYPE' },
  { id: 2, width: '8%', sortKey: 'DisplayStatus', label: 'LBL_PROGRESS' },
  { id: 3, width: '15%', sortKey: 'ModelName', label: 'LBL_MODEL' },
  { id: 4, width: '15%', sortKey: 'SerialNo', label: 'LBL_SERIAL_NO' },
  {
    id: 5,
    width: '7%',
    sortKey: 'DisplayUserName',
    label: 'LBL_INSPECTOR_NAME',
  },
  { id: 6, width: '7%', sortKey: 'Customer', label: 'LBL_CUSTOMER' },
  { id: 7, width: '7%', sortKey: 'Distributor', label: 'LBL_DISTRIBUTOR' },
  {
    id: 8,
    width: '10%',
    sortKey: 'InspectionTermFrom',
    label: 'LBL_START_DATE',
  },
  { id: 9, width: '10%', sortKey: 'InspectionTermTo', label: 'LBL_END_DATE' },
] as const;
/** ソートKeyの型 */
export type SortKeyType = (typeof resultTableColumns)[number]['sortKey'];

/** 修理依頼一覧を表示するテーブル */
const SearchResultTable: React.FC<SearchResultTableProps> = ({
  params,
  updateParams,
  page,
  updatePage,
  repairRequests,
  totalRows,
  onSortChange,
  prepareInspection,
  fetchRepairRequests,
}) => {
  const theme = useTheme();
  const style = {
    result: {
      background: 'background.default',
      padding: '0.5rem',
      fontSize: theme.typography.caption.fontSize,
      minWidth: '100px',
    },
    overFlow: {
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 1,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };
  const { t } = useTranslation();
  const { error, hideError, showApiError } = useErrorModal();
  const saveHistory = useSaveHistory();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const dispatch: AppDispatch = useDispatch();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [selectedRepairRequest, setSelectedRepairRequest] = useState<
    RepairRequestInfo | undefined
  >(undefined);

  const handleSortClick = (sortKey: SortKeyType) => {
    let order: SortOrder = ORDER_TEXT.DESC; // デフォルト降順で設定
    if (sortKey === params.sortKey) {
      order =
        params.order === ORDER_TEXT.DESC ? ORDER_TEXT.ASC : ORDER_TEXT.DESC;
    }
    if (order === ORDER_TEXT.ASC) {
      onSortChange(sortKey, ORDER_TEXT.ASC);
    } else {
      onSortChange(sortKey, ORDER_TEXT.DESC);
    }
  };

  /** 表示用のタイプに変換 */
  const convertToDisplayType = (status: number) => {
    // 0 : 受入診断
    // 1 : 出荷検査
    return status <= INSPECTION_STATUSES.INSPECTION_COMPLETE ? 0 : 1;
  };

  /** 表示用のステータスに変換 */
  const convertToDisplayStatus = (status: number) => {
    // 0 : 進行中
    // 1 : 完了
    return status === INSPECTION_STATUSES.NO_FAULT_FOUND ||
      status === INSPECTION_STATUSES.INSPECTION_COMPLETE ||
      status === INSPECTION_STATUSES.QC_COMPLETE
      ? 1
      : 0;
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    updatePage(newPage);
    fetchRepairRequests({ ...params, rowFrom: newPage * params.perPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updatePage(0);
    updateParams({ ...params, perPage: Number(event.target.value) });
    fetchRepairRequests({
      ...params,
      rowFrom: 0,
      perPage: Number(event.target.value),
    });
  };

  /** 一覧のRMA No.クリック */
  const onClickRmaNo = async (request: RepairRequestInfo) => {
    await prepareInspection(request);
  };

  const isDownwardIcon = (sortKey: SortKeyType) => {
    if (sortKey === params.sortKey) {
      return params.order === ORDER_TEXT.DESC ? true : false;
    }
    return false;
  };

  const isUpwardIcon = (sortKey: SortKeyType) => {
    if (sortKey === params.sortKey) {
      return params.order === ORDER_TEXT.ASC ? true : false;
    }
    return false;
  };

  /** 削除アイコンクリック */
  const onClickDeleteIcon = (repairRequest: RepairRequestInfo) => {
    setSelectedRepairRequest(repairRequest);
    setIsOpenDeleteModal(true);
  };

  /** 削除確認モーダルOK */
  const onClickConfirmDeleteOk = async () => {
    if (!selectedRepairRequest) {
      return;
    }
    setIsOpenDeleteModal(false);
    // 削除実行
    const response = await dispatch(
      deleteRepairRequest(selectedRepairRequest.rmaNo),
    );
    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.REPAIR_REQUEST);
      return;
    }

    /** 操作履歴(修理依頼のDelete)を保存 */
    saveHistory(
      selectedRepairRequest,
      HISTORY_STATUSES.NONE.id,
      HISTORY_TYPE.DELETE.id,
      async () => await fetchRepairRequests(params),
      showApiError,
      JSON.stringify(selectedRepairRequest),
    );
  };

  /** 削除確認モーダルキャンセル */
  const onClickConfirmDeleteCancel = () => {
    setIsOpenDeleteModal(false);
    setSelectedRepairRequest(undefined);
  };

  return (
    <Box mx="2%" mb={`calc(${ADD_BUTTON_SIZE} * 1.5)`}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {[...resultTableColumns]
                .sort((a, b) => a.id - b.id)
                .map((column, i) => (
                  <TableCell
                    key={`repair_requests_header_${i}`}
                    sx={{
                      ...TABLE_HEADER,
                      px: '0.5rem',
                      maxWidth: column.width,
                      cursor: column.sortKey ? 'pointer' : 'default',
                    }}
                    onClick={() => handleSortClick(column.sortKey)}
                  >
                    <Box display="flex" alignItems="center">
                      {`${t(column.label)} `}
                      {isDownwardIcon(column.sortKey) && (
                        <ArrowDownwardIcon
                          style={{ fontSize: 20, left: '5px' }}
                        />
                      )}
                      {isUpwardIcon(column.sortKey) && (
                        <ArrowUpwardIcon
                          style={{ fontSize: 20, left: '5px' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                ))}
              {isAdmin(userData) && (
                <TableCell
                  key={`repair_requests_header_delete`}
                  sx={{
                    ...TABLE_HEADER,
                    px: '0.5rem',
                    cursor: 'default',
                    textAlign: 'center',
                  }}
                >
                  {`${t('LBL_DELETE')}`}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {repairRequests.map((request, i) => (
              <TableRow key={`repair_requests_${i}`} hover>
                <TableCell style={style.result}>
                  <Link
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onClickRmaNo(request)}
                  >
                    {request.rmaNo}
                  </Link>
                </TableCell>
                <TableCell style={style.result}>
                  {t(TYPE[convertToDisplayType(request.status)])}
                </TableCell>
                <TableCell style={style.result}>
                  {t(STATUS[convertToDisplayStatus(request.status)])}
                </TableCell>
                <TableCell style={style.result}>
                  <Box sx={{ whiteSpace: 'nowrap' }}>{request.model}</Box>
                </TableCell>
                <TableCell style={style.result}>
                  <Box sx={{ whiteSpace: 'nowrap' }}>{request.serialNo}</Box>
                </TableCell>
                <TableCell style={style.result} sx={{ maxWidth: '180px' }}>
                  <Box sx={style.overFlow}>{request.inspectorName}</Box>
                </TableCell>
                <TableCell style={style.result} sx={{ maxWidth: '180px' }}>
                  <Box sx={style.overFlow}>{request.customerName}</Box>
                </TableCell>
                <TableCell style={style.result} sx={{ maxWidth: '180px' }}>
                  <Box sx={style.overFlow}>{request.distributor}</Box>
                </TableCell>
                <TableCell style={style.result}>
                  {formatDate(request.startDate, userData.dateFormat)}
                </TableCell>
                <TableCell style={style.result}>
                  {request.endDate
                    ? formatDate(request.endDate, userData.dateFormat)
                    : ''}
                </TableCell>
                {isAdmin(userData) && (
                  <TableCell
                    style={style.result}
                    sx={{ minWidth: '50px !important', width: '50px' }}
                  >
                    <Button onClick={() => onClickDeleteIcon(request)}>
                      <DeleteIcon color="error" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          labelRowsPerPage={t('LBL_ROWS_PER_PAGE')}
          rowsPerPageOptions={ROWS_PER_PAGE_LIST}
          component="div"
          count={totalRows}
          rowsPerPage={params.perPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            t('LBL_DISPLAY_DATA_LABEL', { 0: from, 1: to, 2: count })
          }
        />
      </TableContainer>
      <ConfirmModal
        isOpen={isOpenDeleteModal}
        displayMessage={t('MSG_DELETE_REPAIR_REQUEST')}
        onClickCancel={onClickConfirmDeleteCancel}
        onClickOk={onClickConfirmDeleteOk}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default SearchResultTable;
